import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Upload, Trash2, FileText, Image as ImageIcon, ExternalLink } from 'lucide-react';
import ImageDownloadMenu from './ImageDownloadMenu';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ withCredentials: true });
const MAX = 25 * 1024 * 1024;

const fmtSize = (n) => (!n ? '' : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function DocRow({ a, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-2.5 py-2" data-testid={`doc-${a.asset_id}`}>
      <FileText size={16} className="text-[#1D4ED8] shrink-0" />
      <a href={`${BACKEND}${a.url}`} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 text-xs font-semibold text-[#0A0F1C] truncate hover:text-[#1D4ED8]">
        {a.filename} <span className="text-[#94A3B8] font-normal">{fmtSize(a.size)}</span>
      </a>
      <ExternalLink size={12} className="text-[#94A3B8]" />
      <button type="button" onClick={onRemove} className="text-[#CBD5E1] hover:text-red-500"><Trash2 size={13} /></button>
    </div>
  );
}

export default function AssetUploader({ vision, kind, label, hint, accept, base, q, onChange, multiple = true }) {
  const [busy, setBusy] = useState(0);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const assets = (vision.assets || []).filter((a) => a.kind === kind);
  const isDoc = kind === 'document';

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    for (const file of list) {
      if (file.size > MAX) { toast.error(`${file.name} is over 25MB`); continue; }
      setBusy((b) => b + 1);
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('file', file);
      try {
        const r = await ax.post(`${BACKEND}${base}/${vision.vision_id}/assets${q}`, fd);
        onChange(r.data);
        toast.success(kind === 'logo' ? 'Logo uploaded — brand colors detected' : `${file.name} uploaded`);
      } catch (err) { toast.error(`${file.name}: ${err.response?.data?.detail || err.message}`); }
      finally { setBusy((b) => b - 1); }
    }
    if (inputRef.current) inputRef.current.value = '';
  };
  const remove = async (aid) => {
    try { const r = await ax.delete(`${BACKEND}${base}/${vision.vision_id}/assets/${aid}${q}`); onChange(r.data); }
    catch { toast.error('Could not remove'); }
  };

  return (
    <div data-testid={`asset-${kind}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-bold uppercase tracking-wide text-[#64748B]">{label}</span>
        {busy > 0 && <Loader2 size={13} className="animate-spin text-[#1D4ED8]" />}
      </div>
      {hint && <p className="text-[11px] text-[#94A3B8] mb-1.5">{hint}</p>}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
        data-testid={`asset-dropzone-${kind}`}
        className={`cursor-pointer rounded-lg border-2 border-dashed text-xs py-4 px-2 flex flex-col items-center justify-center gap-1 transition-colors ${drag ? 'border-[#1D4ED8] bg-[#EFF6FF] text-[#1D4ED8]' : 'border-[#CBD5E1] text-[#94A3B8] hover:border-[#1D4ED8] hover:text-[#1D4ED8]'}`}>
        {isDoc ? <FileText size={18} /> : <ImageIcon size={18} />}
        <span className="font-semibold inline-flex items-center gap-1"><Upload size={12} /> Click or drop {isDoc ? 'files' : 'images'}</span>
      </div>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={(e) => uploadFiles(e.target.files)} className="hidden" data-testid={`asset-input-${kind}`} />
      {assets.length > 0 && (isDoc ? (
        <div className="space-y-1.5 mt-2">{assets.map((a) => <DocRow key={a.asset_id} a={a} onRemove={() => remove(a.asset_id)} />)}</div>
      ) : (
        <div className="flex flex-wrap gap-2 mt-2">
          {assets.map((a) => (
            <div key={a.asset_id} className="relative group" data-testid={`asset-thumb-${a.asset_id}`}>
              <a href={`${BACKEND}${a.url}`} target="_blank" rel="noopener noreferrer">
                <img src={`${BACKEND}${a.url}`} alt={a.filename} className="w-16 h-16 object-contain bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]" />
              </a>
              <div className="absolute -bottom-1.5 -right-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"><ImageDownloadMenu visionId={vision.vision_id} srcId={a.asset_id} compact /></div>
              <button type="button" onClick={() => remove(a.asset_id)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
